import React from 'react';
import { cn } from '../lib/utils';
import { theme } from '../config/theme';

const Input = React.forwardRef(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  fullWidth = false,
  multiline = false,
  rows = 3,
  className,
  ...props
}, ref) => {
  const generatedId = React.useId();
  const controlId = props.id || generatedId;

  // Mobile-first:
  // - >=16px font on small screens to avoid iOS zoom
  // - >=44px touch target on small screens
  const baseClasses = 'block w-full px-3 py-3 sm:py-2 text-base sm:text-sm border rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand)]/40 transition-colors duration-200 max-sm:min-h-11';
  
  const errorClasses = error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : '';
  const iconClasses = leftIcon ? 'pl-10' : rightIcon ? 'pr-10' : '';
  
  const classes = cn(
    baseClasses,
    errorClasses,
    iconClasses,
    fullWidth && 'w-full',
    className
  );
  
  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label htmlFor={controlId} className="block text-sm font-medium mb-1" style={{ color: theme.text }}>
          {label}
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className="h-5 w-5 text-gray-400">
              {leftIcon}
            </div>
          </div>
        )}
        
        {multiline ? (
          <textarea
            ref={ref}
            id={controlId}
            className={classes}
            style={{
              background: theme.bgCard,
              color: theme.text,
              borderColor: error ? '#fecaca' : theme.border,
              boxShadow: '0 0 0 0 rgba(0,0,0,0)'
            }}
            rows={rows}
            {...props}
          />
        ) : (
          <input
            ref={ref}
            id={controlId}
            className={classes}
            style={{
              background: theme.bgCard,
              color: theme.text,
              borderColor: error ? '#fecaca' : theme.border,
              boxShadow: '0 0 0 0 rgba(0,0,0,0)',
              colorScheme: theme.mode === 'dark' ? 'dark' : 'light'
            }}
            {...props}
          />
        )}
        
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="h-5 w-5" style={{ color: theme.textSoft }}>
              {rightIcon}
            </div>
          </div>
        )}
      </div>
      
      {(error || helperText) && (
        <p className={cn(
          'mt-1 text-sm',
          error ? 'text-red-600' : ''
        )} style={{ color: error ? '#dc2626' : theme.textSoft }}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
